import React, { useState, useMemo } from 'react'
import { InfoIcon, colors } from '@resiliatech/birdsong'
import { IconButton, Tooltip, Typography } from '@material-ui/core'
import { GoalGroupChartData } from 'Generated/graphql-types'
import { SkeletonLoader } from 'Components/SharedComponents/SkeletonLoader/SkeletonLoader'
import { LineSeriesType } from '@mui/x-charts'
import moment from 'moment'
import { analytics } from 'Modules/Analytics'
import { useAppState } from 'Context/AppContext/AppContext'
import { funderGoals } from 'Modules/Analytics/trackActionInterface/FunderPlatform/funderGoals'
import { toTitleCase } from 'Utils/formatters'
import {
  ChartHeader,
  ChipProgressText,
  ChipTotalText,
  Header,
  HeaderContainer,
  OverallProgressChip,
  AggregateToggle,
  ToggleLabel,
  TitleToggleContainer,
  StyledSwitch,
  ChartOverviewHeader,
  ChartContainer,
  ChartSectionContainer,
} from './FunderChart.styled'
import {
  createAggregateSeriesData,
  createFormattedSeriesData,
  formatLargeNumber,
} from '../utils'
import { ChartFilters } from './ChartFilters'
import { FunderChart } from './FunderChart'
import { GoalGroupChartType } from './types'

type FunderChartContainerProps = {
  chartChipText: string
  chartChipTargetDescription?: string
  chartContainerHeader: string
  chartTooltipText: string
  chartData: GoalGroupChartData
  chartType: GoalGroupChartType
  goalGroupId: number | null
  loading: boolean
}

export const FunderChartContainer = ({
  chartChipText,
  chartChipTargetDescription,
  chartContainerHeader,
  chartTooltipText,
  chartData,
  chartType,
  goalGroupId,
  loading,
}: FunderChartContainerProps) => {
  const {
    userConfig: { id, email },
  } = useAppState()
  const [filterValues, setFilterValues] = useState({
    nonprofitOrgNames: [] as string[],
  })
  const [showAggregatedData, setShowAggregatedData] = useState(true)

  const formatDate = (dateString: string) =>
    moment(dateString).format('MM/DD/YYYY')

  const [xLabels, formattedXLabels] = useMemo(() => {
    const allDates = chartData?.seriesData.flatMap((s) =>
      s.data.map((point) => point.x)
    )

    const uniqueSortedDates = [...new Set(allDates)].sort()

    const formatted = uniqueSortedDates.map(formatDate)

    return [uniqueSortedDates, formatted]
  }, [chartData])

  const formattedSeriesData = useMemo(
    () => createFormattedSeriesData(chartData, xLabels),
    [chartData, xLabels]
  )

  const aggregateSeriesData = useMemo(
    () => createAggregateSeriesData(chartData, xLabels),
    [chartData, xLabels]
  )

  const allNonprofitOrgNames = [
    ...new Set(formattedSeriesData?.map((series) => series.label)),
  ]

  const getFilteredSeries = () => {
    if (showAggregatedData && chartType === GoalGroupChartType.goalProgress) {
      return aggregateSeriesData
    }
    if (filterValues.nonprofitOrgNames.length > 0) {
      return formattedSeriesData.filter((series) =>
        filterValues.nonprofitOrgNames.includes(series.label)
      )
    }
    return formattedSeriesData
  }

  const filteredSeries = getFilteredSeries()

  const totalAmount = chartData?.totalValue || 0

  const toggleLabel = showAggregatedData ? 'On Aggregate' : 'By Grantee'

  const shouldShowLegendOrFilters =
    !showAggregatedData || chartType === GoalGroupChartType.taskCompletion

  const dispatchChartToggleClicked = () => {
    analytics.trackActionCreator(
      funderGoals.Funder_Collab_Goal_Chart_Toggle_Clicked
    )({
      goalId: chartData.goalId,
      userId: id,
      userEmail: email,
      chartType,
      toggleLabel,
    })
  }

  const handleToggleChange = () => {
    dispatchChartToggleClicked()
    setShowAggregatedData(!showAggregatedData)
  }

  return (
    <ChartSectionContainer>
      {loading ? (
        <SkeletonLoader />
      ) : (
        <>
          <ChartHeader>
            <ChartOverviewHeader>{chartContainerHeader}</ChartOverviewHeader>
            <OverallProgressChip>
              <ChipProgressText>{chartChipText}</ChipProgressText>
              <Tooltip
                title={<Typography>{chartTooltipText}</Typography>}
                arrow
                placement='top'
              >
                <IconButton size='small'>
                  <InfoIcon
                    size='small'
                    color={colors.interactive.disabledDark}
                  />
                </IconButton>
              </Tooltip>
              <ChipTotalText>
                {formatLargeNumber(totalAmount)}{' '}
                {toTitleCase(chartChipTargetDescription)}
              </ChipTotalText>
            </OverallProgressChip>
          </ChartHeader>
          <ChartContainer>
            <HeaderContainer>
              <TitleToggleContainer>
                <Header>{chartData.title}</Header>
                {chartType === GoalGroupChartType.goalProgress && (
                  <AggregateToggle>
                    <ToggleLabel>{toggleLabel}</ToggleLabel>
                    <StyledSwitch
                      checked={showAggregatedData}
                      onChange={() => handleToggleChange()}
                    />
                  </AggregateToggle>
                )}
              </TitleToggleContainer>
              {shouldShowLegendOrFilters && (
                <ChartFilters
                  chartType={chartType}
                  filterValues={filterValues}
                  goalGroupId={goalGroupId}
                  setFilterValues={setFilterValues}
                  allNonprofitOrgNames={allNonprofitOrgNames}
                />
              )}
            </HeaderContainer>
            <FunderChart
              chartType={chartType}
              series={
                showAggregatedData &&
                chartType === GoalGroupChartType.goalProgress
                  ? (aggregateSeriesData as LineSeriesType[])
                  : (filteredSeries as LineSeriesType[])
              }
              target={chartData.target || 0}
              unitOfMeasurement={toTitleCase(chartChipTargetDescription) || ''}
              xLabels={formattedXLabels}
              shouldShowLegend={shouldShowLegendOrFilters}
            />
          </ChartContainer>
        </>
      )}
    </ChartSectionContainer>
  )
}
